import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      setCart({ items: [] });
    }
  }, [isAuthenticated]);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API_URL}/cart`);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    }
  };

  const addToCart = async (productId, quantity = 1, customization = null) => {
    try {
      const payload = { 
        product_id: productId, 
        quantity 
      };
      
      // Add customization data if provided
      if (customization) {
        payload.custom_config = customization.customConfig || null;
        payload.custom_price = customization.customPrice || null;
      }
      
      await axios.post(`${API_URL}/cart/add`, payload);
      await fetchCart();
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await axios.post(`${API_URL}/cart/remove?product_id=${productId}`);
      await fetchCart();
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      await axios.post(`${API_URL}/cart/clear`);
      await fetchCart();
    } catch (error) {
      console.error('Failed to clear cart:', error);
      throw error;
    }
  };

  const cartCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const cartValue = useMemo(
    () => ({ cart, addToCart, removeFromCart, clearCart, cartCount, fetchCart }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cart, cartCount]
  );

  return (
    <CartContext.Provider value={cartValue}>
      {children}
    </CartContext.Provider>
  );
};